/*
 * Urban Customz premade collection cards (snippets/uc-design-card.liquid).
 *
 * Each card scrolls into view → one small JSON per design (families, prices, colours, mockup URL
 * template) from the customizer app → garment chips and colour swatches become live. A chip or swatch
 * swaps the picture (a pre-rendered file; crossfade; shimmer only while a cold render takes a moment),
 * updates the price and rewrites THAT card's product link with ?style=&color= so the product page opens
 * on the same garment and colour. Nothing is stored (no sessionStorage, no localStorage, no cookie) and
 * no other link on the page is touched.
 *
 * Runs on the grids that render the card (collection, search); re-scans when the grid changes
 * (filters, sorting, load-more). No dependencies.
 */
(function () {
  "use strict";
  if (window.ucDesignCards) {
    window.ucDesignCards.scan();
    return;
  }
  var CACHE_TTL = 10 * 60 * 1000;
  var mem = {};
  var inflight = {};
  // Card data is fetched two at a time: each answer the app has not cached yet costs it a design load,
  // and a page of sixteen cards must never open sixteen of those at once (pooler outage, 2026-10-09).
  var MAX_FETCH = 2;
  var active = 0;
  var queue = [];
  function schedule(fn) {
    return new Promise(function (resolve, reject) {
      queue.push(function () {
        active += 1;
        fn().then(resolve, reject);
      });
      next();
    });
  }
  function next() {
    while (queue.length && active < MAX_FETCH) queue.shift()();
  }
  function release() { active = Math.max(0, active - 1); next(); }
  var narrow = window.matchMedia("(max-width: 749px)");
  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)");

  function money(format, cents) {
    var n = cents / 100;
    var s = format || "${{amount}}";
    return s
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, String(Math.round(n)))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, n.toFixed(2).replace(".", ","))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, String(Math.round(n)))
      .replace(/\{\{\s*amount\s*\}\}/g, n.toFixed(2));
  }

  function fetchCard(card) {
    var slug = card.getAttribute("data-slug");
    var app = card.getAttribute("data-app");
    // Page-lifetime memory only. Nothing is written to sessionStorage or localStorage: the card data is
    // edge-cached by the app, and a stored context is how the quick-view bug once leaked onto other links.
    if (mem[slug] && mem[slug].at > Date.now() - CACHE_TTL) return Promise.resolve(mem[slug].data);
    if (inflight[slug]) return inflight[slug];
    inflight[slug] = schedule(function () {
      return fetch(app + "/api/premade/designs/" + encodeURIComponent(slug) + "/card", { mode: "cors", credentials: "omit" })
        .then(function (r) {
          if (!r.ok) throw new Error("HTTP " + r.status);
          return r.json();
        })
        .then(function (data) {
          release();
          mem[slug] = { at: Date.now(), data: data };
          delete inflight[slug];
          return data;
        })
        .catch(function (e) {
          release();
          delete inflight[slug];
          throw e;
        });
    });
    return inflight[slug];
  }

  /* ------------------------------------------------------------------ one card */
  function Card(el) {
    this.el = el;
    this.data = null;
    this.family = null;
    this.color = null;
    this.familyEl = el.querySelector("[data-uc-dc-families]");
    this.colorEl = el.querySelector("[data-uc-dc-colors]");
    this.priceEl = el.querySelector("[data-uc-dc-price]");
    this.forEl = el.querySelector("[data-uc-dc-for]");
    this.media = el.querySelector(".uc-dc__media");
    this.links = el.querySelectorAll("[data-uc-dc-link]");
    this.baseUrl = el.getAttribute("data-url");
    this.format = el.getAttribute("data-money") || "${{amount}}";
    this.seq = 0;
    this.touched = false;
  }

  Card.prototype.load = function () {
    var self = this;
    if (this.data || this.loading) return;
    this.loading = true;
    fetchCard(this.el)
      .then(function (data) {
        self.data = data;
        self.loading = false;
        if (!data.families || !data.families.length) return;
        // Start where the server-rendered card started (the first variant), so nothing changes on load.
        var startStyle = self.el.getAttribute("data-style");
        var startColor = self.el.getAttribute("data-color");
        var fam = familyOfStyle(data, startStyle) || data.families[0];
        self.family = fam;
        self.color = fam.colors.some(function (c) { return c.name === startColor; }) ? startColor : fam.colors[0] ? fam.colors[0].name : null;
        self.render();
      })
      .catch(function () {
        self.loading = false;
        // The static chips stay as they were rendered: still informative, just not interactive.
      });
  };

  function familyOfStyle(data, style) {
    for (var i = 0; i < data.families.length; i++) {
      var f = data.families[i];
      for (var j = 0; j < f.fits.length; j++) if (f.fits[j].styleValue === style) return f;
    }
    return null;
  }

  Card.prototype.visibleFamilies = function () {
    var max = narrow.matches ? 2 : 3;
    var fams = this.data.families;
    if (fams.length <= max + 1) return fams.slice(); // "+1" would hide exactly one: show it instead
    var out = fams.slice(0, max);
    if (out.indexOf(this.family) === -1) out[max - 1] = this.family;
    return out;
  };

  Card.prototype.visibleColors = function () {
    var max = narrow.matches ? 4 : 5;
    var cols = this.family.colors;
    if (cols.length <= max + 1) return cols.slice();
    var out = cols.slice(0, max);
    var sel = this.selectedColor();
    if (sel && out.indexOf(sel) === -1) out[max - 1] = sel;
    return out;
  };

  Card.prototype.selectedColor = function () {
    var name = this.color;
    for (var i = 0; i < this.family.colors.length; i++) if (this.family.colors[i].name === name) return this.family.colors[i];
    return null;
  };

  Card.prototype.render = function () {
    var self = this;
    var data = this.data;
    if (this.el.getAttribute("data-wall") === "1" || data.kind === "wall") {
      if (this.familyEl) this.familyEl.hidden = true;
      if (this.colorEl) this.colorEl.hidden = true;
      return;
    }
    if (!this.familyEl || !this.colorEl) return;
    // Families
    var vis = this.visibleFamilies();
    var hidden = data.families.length - vis.length;
    this.familyEl.innerHTML = "";
    vis.forEach(function (f) {
      var b = document.createElement("button");
      b.type = "button";
      b.className = "uc-dc__chip" + (f === self.family ? " is-selected" : "");
      b.setAttribute("aria-pressed", f === self.family ? "true" : "false");
      b.textContent = f.label;
      b.title = f.label + " · " + money(self.format, f.priceCents);
      b.addEventListener("click", function (e) {
        e.preventDefault();
        self.setFamily(f);
      });
      self.familyEl.appendChild(b);
    });
    if (hidden > 0) {
      var more = document.createElement("button");
      more.type = "button";
      more.className = "uc-dc__chip uc-dc__chip--more";
      more.textContent = "+" + hidden;
      more.setAttribute("aria-label", "All " + data.families.length + " styles");
      more.setAttribute("aria-haspopup", "dialog");
      more.addEventListener("click", function (e) {
        e.preventDefault();
        Popover.open(self, "families", more);
      });
      this.familyEl.appendChild(more);
    }
    // Colours
    var cols = this.visibleColors();
    var hiddenCols = this.family.colors.length - cols.length;
    this.colorEl.innerHTML = "";
    if (this.family.preview === "flat") {
      var note = document.createElement("span");
      note.className = "uc-dc__colors-note";
      note.textContent = this.family.colors.map(function (c) { return c.name; }).join(" · ");
      this.colorEl.appendChild(note);
    } else {
      cols.forEach(function (c) {
        self.colorEl.appendChild(self.swatch(c, c.name === self.color));
      });
      if (hiddenCols > 0) {
        var moreC = document.createElement("button");
        moreC.type = "button";
        moreC.className = "uc-dc__swatch uc-dc__swatch--more";
        moreC.textContent = "+" + hiddenCols;
        moreC.setAttribute("aria-label", "All " + self.family.colors.length + " colours");
        moreC.setAttribute("aria-haspopup", "dialog");
        moreC.addEventListener("click", function (e) {
          e.preventDefault();
          Popover.open(self, "colors", moreC);
        });
        self.colorEl.appendChild(moreC);
      }
    }
    // Price and the garment it is for
    this.priceEl.textContent = money(this.format, this.family.priceCents);
    if (this.forEl) this.forEl.textContent = this.family.label + (this.family.preview === "flat" ? " · from" : "");
    this.updateLinks();
  };

  Card.prototype.swatch = function (c, selected) {
    var self = this;
    var b = document.createElement("button");
    b.type = "button";
    b.className = "uc-dc__swatch" + (selected ? " is-selected" : "") + (c.hex2 ? " uc-dc__swatch--two" : "");
    b.setAttribute("aria-pressed", selected ? "true" : "false");
    b.setAttribute("aria-label", c.name);
    b.title = c.name;
    b.style.setProperty("--sw", c.hex || "#d9d9d9");
    if (c.hex2) b.style.setProperty("--sw2", c.hex2);
    b.addEventListener("click", function (e) {
      e.preventDefault();
      self.setColor(c.name);
    });
    return b;
  };

  Card.prototype.setFamily = function (f) {
    if (!f) return;
    this.touched = true;
    var keep = f.colors.some(function (c) { return c.name === this.color; }, this);
    this.family = f;
    this.color = keep ? this.color : f.colors[0] ? f.colors[0].name : null;
    this.render();
    this.showMockup();
  };

  Card.prototype.setColor = function (name) {
    this.touched = true;
    this.color = name;
    this.render();
    this.showMockup();
  };

  /** A stored picture: a plain file in the public bucket (no app request). */
  Card.prototype.storedUrl = function (w) {
    var c = this.selectedColor();
    if (!this.data.storage || !c || !c.key) return null;
    return this.data.storage + this.family.productId + "/" + c.key + ".w" + w + ".webp";
  };
  /** The render route: makes the picture once when the bucket has none yet. */
  Card.prototype.renderUrl = function (w) {
    return this.data.mockup.replace("{style}", encodeURIComponent(this.family.styleValue)).replace("{color}", encodeURIComponent(this.color)).replace("{w}", String(w));
  };

  Card.prototype.showMockup = function () {
    var self = this;
    if (!this.family || !this.color) return;
    var seq = ++this.seq;
    var current = this.media.querySelector(".uc-dc__img.is-current");
    var img = document.createElement("img");
    img.className = "uc-dc__img is-entering";
    img.alt = current ? current.alt.replace(/ on a .*$/, "") + " on a " + this.color + " " + this.family.styleValue : "";
    img.width = 720;
    img.height = 900;
    img.decoding = "async";
    var fallbackTimer = setTimeout(function () {
      if (self.seq === seq) self.media.classList.add("is-loading");
    }, 220);
    var done = function (ok) {
      clearTimeout(fallbackTimer);
      if (self.seq !== seq) return; // a newer choice won
      self.media.classList.remove("is-loading");
      if (!ok) return; // keep the picture we had (the chip still shows the price and the link preselects)
      self.media.insertBefore(img, self.media.firstChild);
      var old = self.media.querySelectorAll(".uc-dc__img.is-current");
      requestAnimationFrame(function () {
        img.classList.remove("is-entering");
        img.classList.add("is-current");
        for (var i = 0; i < old.length; i++) {
          (function (o) {
            o.classList.remove("is-current");
            o.classList.add("is-leaving");
            setTimeout(function () { if (o.parentNode) o.parentNode.removeChild(o); }, reduce.matches ? 0 : 240);
          })(old[i]);
        }
      });
    };
    var tried = 0;
    var stored = this.storedUrl(720);
    img.onload = function () { done(true); };
    img.onerror = function () {
      // Not in the bucket yet: ask the app to render it once. A second failure keeps the current picture.
      if (tried === 0 && stored) {
        tried = 1;
        img.srcset = self.renderUrl(360) + " 360w, " + self.renderUrl(720) + " 720w";
        img.src = self.renderUrl(720);
        return;
      }
      done(false);
    };
    img.sizes = current && current.sizes ? current.sizes : "50vw";
    if (stored) {
      img.srcset = this.storedUrl(360) + " 360w, " + stored + " 720w";
      img.src = stored;
    } else {
      tried = 1;
      img.srcset = this.renderUrl(360) + " 360w, " + this.renderUrl(720) + " 720w";
      img.src = this.renderUrl(720);
    }
  };

  Card.prototype.updateLinks = function () {
    if (!this.touched) return;
    var href = this.baseUrl + "?style=" + encodeURIComponent(this.family.styleValue) + "&color=" + encodeURIComponent(this.color || "");
    for (var i = 0; i < this.links.length; i++) this.links[i].setAttribute("href", href);
  };

  /* ------------------------------------------------------------------ popover (one, shared) */
  var Popover = {
    el: null,
    card: null,
    opener: null,
    open: function (card, kind, opener) {
      this.close();
      var self = this;
      var pop = document.createElement("div");
      pop.className = "uc-dc-pop";
      pop.setAttribute("role", "dialog");
      pop.setAttribute("aria-label", kind === "families" ? "Styles" : "Colours");
      if (kind === "families") {
        var list = document.createElement("div");
        list.className = "uc-dc-pop__list";
        card.data.families.forEach(function (f) {
          var row = document.createElement("button");
          row.type = "button";
          row.className = "uc-dc-pop__row" + (f === card.family ? " is-selected" : "");
          var fits = f.fits.map(function (x) { return x.audience === "men" ? "men's" : x.audience === "women" ? "women's" : x.audience === "kids" ? "kids'" : x.audience; });
          row.innerHTML = '<span class="uc-dc-pop__label">' + esc(f.label) + "</span>" + '<span class="uc-dc-pop__fits">' + esc(fits.join(" · ")) + "</span>" + '<span class="uc-dc-pop__price">' + esc(money(card.format, f.priceCents)) + "</span>";
          row.addEventListener("click", function () {
            self.close();
            card.setFamily(f);
          });
          list.appendChild(row);
        });
        pop.appendChild(list);
      } else {
        var grid = document.createElement("div");
        grid.className = "uc-dc-pop__swatches";
        card.family.colors.forEach(function (c) {
          var b = card.swatch(c, c.name === card.color);
          b.addEventListener("click", function () { self.close(); });
          var wrap = document.createElement("span");
          wrap.className = "uc-dc-pop__sw";
          wrap.appendChild(b);
          var name = document.createElement("span");
          name.className = "uc-dc-pop__name";
          name.textContent = c.name;
          wrap.appendChild(name);
          grid.appendChild(wrap);
        });
        pop.appendChild(grid);
      }
      var close = document.createElement("button");
      close.type = "button";
      close.className = "uc-dc-pop__close";
      close.setAttribute("aria-label", "Close");
      close.innerHTML = "&times;";
      close.addEventListener("click", function () { self.close(); });
      pop.appendChild(close);
      card.el.appendChild(pop);
      card.el.classList.add("has-pop");
      // The grid item (Dawn's reveal animation leaves a stacking context on it) must rise above the
      // next row, or the popover paints underneath the cards below.
      var item = card.el.closest(".grid__item, li");
      if (item) item.classList.add("has-pop");
      this.el = pop;
      this.card = card;
      this.opener = opener;
      var first = pop.querySelector("button");
      if (first) first.focus({ preventScroll: true });
      setTimeout(function () {
        document.addEventListener("click", self.onDoc, true);
        document.addEventListener("keydown", self.onKey);
        window.addEventListener("scroll", self.onScroll, { passive: true });
      }, 0);
    },
    close: function () {
      if (!this.el) return;
      var el = this.el;
      var opener = this.opener;
      this.card.el.classList.remove("has-pop");
      var item = this.card.el.closest(".grid__item, li");
      if (item) item.classList.remove("has-pop");
      if (el.parentNode) el.parentNode.removeChild(el);
      this.el = null;
      this.card = null;
      this.opener = null;
      document.removeEventListener("click", this.onDoc, true);
      document.removeEventListener("keydown", this.onKey);
      window.removeEventListener("scroll", this.onScroll);
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    },
    onDoc: function (e) {
      if (Popover.el && !Popover.el.contains(e.target)) Popover.close();
    },
    onKey: function (e) {
      if (e.key === "Escape") {
        e.preventDefault();
        Popover.close();
      }
    },
    onScroll: (function () {
      var start = null;
      return function () {
        if (!Popover.el) { start = null; return; }
        if (start === null) start = window.scrollY;
        if (Math.abs(window.scrollY - start) > 160) { start = null; Popover.close(); }
      };
    })(),
  };

  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (ch) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]; });
  }

  /* ------------------------------------------------------------------ wiring */
  var cards = [];
  var io = "IntersectionObserver" in window
    ? new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var card = entry.target.__ucCard;
          if (card) card.load();
          io.unobserve(entry.target);
        });
      }, { rootMargin: "320px 0px" })
    : null;

  function arm(el) {
    if (el.__ucCard) return;
    var card = new Card(el);
    el.__ucCard = card;
    cards.push(card);
    if (el.getAttribute("data-wall") === "1") return; // nothing to make live on a wall-art card
    if (io) io.observe(el);
    else card.load();
  }

  // The grid changes in two ways: filters / sorting replace the whole grid (the container's children),
  // and load-more appends cards to the list. Only those are watched (no subtree): a picture swap or
  // the popover opening inside a card must not re-scan, and must never close the popover it just opened.
  var watched = [];
  function watch(el, onChange) {
    if (!el || watched.indexOf(el) > -1 || !("MutationObserver" in window)) return;
    watched.push(el);
    new MutationObserver(onChange).observe(el, { childList: true });
  }
  function scan() {
    var els = document.querySelectorAll("[data-uc-dc]");
    for (var i = 0; i < els.length; i++) arm(els[i]);
    var boxes = document.querySelectorAll("#ProductGridContainer, .uc-dc-grid");
    for (var j = 0; j < boxes.length; j++) {
      watch(boxes[j], function () { Popover.close(); scan(); });
      var lists = boxes[j].querySelectorAll("ul.product-grid");
      for (var k = 0; k < lists.length; k++) watch(lists[k], scan);
    }
  }

  narrow.addEventListener && narrow.addEventListener("change", function () {
    cards.forEach(function (c) { if (c.data) c.render(); });
  });

  /*
   * Sorting with the desktop select in Dawn's drawer filter mode: Dawn submits the sort form alone and
   * drops every active filter (its handler only merges FacetFiltersForm, which the drawer mode does not
   * render). The URL is the source of truth for the active filters (Dawn and the category rail both push
   * it), so the new sort is applied on top of it. Runs before Dawn's own listener and stops it.
   */
  document.addEventListener(
    "input",
    function (e) {
      var sel = e.target;
      if (!sel || sel.tagName !== "SELECT" || sel.name !== "sort_by" || !sel.closest("#FacetSortDrawerForm")) return;
      var F = customElements.get("facet-filters-form");
      if (!F || typeof F.renderPage !== "function") return;
      var params = new URLSearchParams(window.location.search);
      params.set("sort_by", sel.value);
      params.delete("page");
      e.stopPropagation();
      F.renderPage(params.toString(), null, true);
    },
    true
  );

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", scan);
  else scan();
  document.addEventListener("shopify:section:load", scan);
  window.ucDesignCards = { scan: scan };
})();
